/* ═══════════════════════════ colour, by name ═══════════════════════════

   The fixed palettes: the colours a course can be, the colours a note can be
   under each template, and the fields a design override may set.

   Every template's note palette is listed in full rather than derived, because
   these are chosen colours rather than computed ones — a note in the Bauhaus
   template is that red because the poster it came from is that red, and no
   formula would arrive at it.

   Data only. Nothing here does anything. */

export const COURSE_COLORS = ['#3d63b6', '#1c7a5b', '#a9701a', '#7250cd', '#bd463f', '#1a7a91', '#a8438a', '#5a8720'];

/* the pale on-dark palette these replaced, index-for-index, so courses saved
   before the cream theme get remapped instead of staying washed out */
export const LEGACY_COURSE_COLORS = ['#7aa2ff', '#4ec9a4', '#f2b45c', '#a78bfa', '#f27a7a', '#5ec8e5', '#e58ac8', '#9fd356'];

export const NOTE_PALETTE = [
  { key: 'slate',  bg: '#eceff5', bar: 'rgba(47,42,33,.07)', line: '#c1cad9' },
  { key: 'amber',  bg: '#fbeaba', bar: 'rgba(47,42,33,.07)', line: '#dfbf63' },
  { key: 'green',  bg: '#e0efdd', bar: 'rgba(47,42,33,.07)', line: '#a4c99e' },
  { key: 'blue',   bg: '#dfeaf8', bar: 'rgba(47,42,33,.07)', line: '#a1c0e3' },
  { key: 'violet', bg: '#eae1f8', bar: 'rgba(47,42,33,.07)', line: '#bca5e4' },
  { key: 'rose',   bg: '#fadfdf', bar: 'rgba(47,42,33,.07)', line: '#e6a8a8' },
];

/* ─────────────────────────── templates ───────────────────────────

   A template is a whole look for the app: `data-template` on <html> is all
   that switches, and the rest is CSS. Notes are the one exception — their
   colour arrives as an inline custom property, so a template that wants
   different note colours has to say so here, keyed identically so a note
   written under one template keeps its identity under the other. */

/* Flat poster ink, six-for-six against NOTE_PALETTE. `text` is set where the
   ground is too dark for ink. */
export const BAUHAUS_NOTE_PALETTE = [
  { key: 'slate',  bg: '#14120f', bar: 'rgba(251,246,234,.16)', line: '#14120f', text: '#fbf6ea' },
  { key: 'amber',  bg: '#f2a81d', bar: 'rgba(20,18,15,.16)',    line: '#14120f' },
  { key: 'green',  bg: '#2c6e63', bar: 'rgba(251,246,234,.18)', line: '#14120f', text: '#fbf6ea' },
  { key: 'blue',   bg: '#1b44a0', bar: 'rgba(251,246,234,.18)', line: '#14120f', text: '#fbf6ea' },
  { key: 'violet', bg: '#2e8a83', bar: 'rgba(251,246,234,.18)', line: '#14120f', text: '#fbf6ea' },
  { key: 'rose',   bg: '#d8402a', bar: 'rgba(251,246,234,.18)', line: '#14120f', text: '#fbf6ea' },
];

/* Swiss: white cards, one red, and grey doing all the other work. The colour
   lives in a bar at the top of the card rather than in the fill. */
export const SWISS_NOTE_PALETTE = [
  { key: 'slate',  bg: '#ffffff', bar: 'rgba(28,28,28,.06)', line: '#1c1c1c', swatch: '#1c1c1c' },
  { key: 'amber',  bg: '#ffffff', bar: 'rgba(28,28,28,.06)', line: '#e8b923', swatch: '#e8b923' },
  { key: 'green',  bg: '#ffffff', bar: 'rgba(28,28,28,.06)', line: '#1f7a4d', swatch: '#1f7a4d' },
  { key: 'blue',   bg: '#ffffff', bar: 'rgba(28,28,28,.06)', line: '#0b5fa5', swatch: '#0b5fa5' },
  { key: 'violet', bg: '#ffffff', bar: 'rgba(28,28,28,.06)', line: '#6b6b6b', swatch: '#6b6b6b' },
  { key: 'rose',   bg: '#ffffff', bar: 'rgba(28,28,28,.06)', line: '#e02200', swatch: '#e02200' },
];

/* Blank: neutral tints only, nothing to interpret. */
export const BLANK_NOTE_PALETTE = [
  { key: 'slate',  bg: '#f1f3f5', bar: 'rgba(0,0,0,.05)', line: '#d6dade' },
  { key: 'amber',  bg: '#fdf3dc', bar: 'rgba(0,0,0,.05)', line: '#e5d3a8' },
  { key: 'green',  bg: '#e9f5ec', bar: 'rgba(0,0,0,.05)', line: '#bcdcc4' },
  { key: 'blue',   bg: '#e8f1fb', bar: 'rgba(0,0,0,.05)', line: '#bdd5ee' },
  { key: 'violet', bg: '#f0ecfa', bar: 'rgba(0,0,0,.05)', line: '#cfc4e8' },
  { key: 'rose',   bg: '#fdecec', bar: 'rgba(0,0,0,.05)', line: '#f0c3c3' },
];

/* Soft UI: one colour, shape from light alone. Notes are that colour too, so the
   identifying colour has to live somewhere visible — here, the head bar. */
export const NEUMORPH_NOTE_PALETTE = [
  { key: 'slate',  bg: '#e4e9f2', bar: '#8a94ab', line: 'transparent', swatch: '#8a94ab' },
  { key: 'amber',  bg: '#f2ecdd', bar: '#c99a3a', line: 'transparent', swatch: '#c99a3a' },
  { key: 'green',  bg: '#dfeee7', bar: '#4a9b77', line: 'transparent', swatch: '#4a9b77' },
  { key: 'blue',   bg: '#dfe6f6', bar: '#5b7cfa', line: 'transparent', swatch: '#5b7cfa' },
  { key: 'violet', bg: '#e8e2f6', bar: '#a06bfa', line: 'transparent', swatch: '#a06bfa' },
  { key: 'rose',   bg: '#f6e2e4', bar: '#d4626e', line: 'transparent', swatch: '#d4626e' },
];

/* Glass: translucent fills, so the tint has to stay light enough to read through. */
export const GLASS_NOTE_PALETTE = [
  { key: 'slate',  bg: 'rgba(255,255,255,.55)',  bar: 'rgba(23,32,58,.20)',  line: 'rgba(255,255,255,.75)', swatch: '#8b93ad' },
  { key: 'amber',  bg: 'rgba(255,224,150,.52)',  bar: 'rgba(150,105,20,.26)', line: 'rgba(255,255,255,.75)', swatch: '#e0a92b' },
  { key: 'green',  bg: 'rgba(150,240,215,.48)',  bar: 'rgba(18,115,90,.24)',  line: 'rgba(255,255,255,.75)', swatch: '#20a882' },
  { key: 'blue',   bg: 'rgba(160,190,255,.50)',  bar: 'rgba(40,70,190,.24)',  line: 'rgba(255,255,255,.75)', swatch: '#4560d8' },
  { key: 'violet', bg: 'rgba(215,170,250,.48)',  bar: 'rgba(140,50,180,.24)', line: 'rgba(255,255,255,.75)', swatch: '#b04ad0' },
  { key: 'rose',   bg: 'rgba(255,175,190,.50)',  bar: 'rgba(180,40,70,.24)',  line: 'rgba(255,255,255,.75)', swatch: '#d94a63' },
];

/* Post-its on warm paper. The tints are a shade stronger than NOTE_PALETTE's
   because they sit on cream rather than white and have to read as coloured paper
   rather than as a wash. `line` is deliberately the same lead for all six: in
   Sketch a note's edge is drawn, and six different pencil colours would undo
   that. The identity moves entirely onto the fill, which is the same trade
   Bauhaus makes in the other direction. */
export const SKETCH_NOTE_PALETTE = [
  { key: 'slate',  bg: '#e9e7e0', bar: 'rgba(45,45,45,.09)', line: '#2d2d2d' },
  { key: 'amber',  bg: '#fff3ae', bar: 'rgba(45,45,45,.09)', line: '#2d2d2d' },
  { key: 'green',  bg: '#d9f0cc', bar: 'rgba(45,45,45,.09)', line: '#2d2d2d' },
  { key: 'blue',   bg: '#d4e7f8', bar: 'rgba(45,45,45,.09)', line: '#2d2d2d' },
  { key: 'violet', bg: '#e7dcf6', bar: 'rgba(45,45,45,.09)', line: '#2d2d2d' },
  { key: 'rose',   bg: '#fbd8d3', bar: 'rgba(45,45,45,.09)', line: '#2d2d2d' },
];

/* Monochrome's hard problem, and the answer it settles on.

   The style forbids colour outright, but a note's colour is the USER'S — six
   identities they filed things under, and a filter they use to find them again.
   Erasing it to satisfy the palette would delete information the app does not
   own, so identity moves from hue onto VALUE: six steps of neutral grey.

   It arrives through `line` rather than `bg` because monochrome.css spends the
   note's fill on white paper and carries the identity as a nine-pixel band
   across the top edge — a masthead rule, which is the most editorial place on a
   white card to put one. That means every `bg` here is the same white, and THAT
   is why all six must state `swatch`: swatchOf() falls back to bg, and six
   identical white chips would leave the colour filter and the legend unusable.
   The swatch is the band's own grey. */
export const MONOCHROME_NOTE_PALETTE = [
  { key: 'slate',  bg: '#ffffff', bar: 'rgba(0,0,0,.05)', line: '#000000', swatch: '#000000' },
  { key: 'amber',  bg: '#ffffff', bar: 'rgba(0,0,0,.05)', line: '#3d3d3d', swatch: '#3d3d3d' },
  { key: 'green',  bg: '#ffffff', bar: 'rgba(0,0,0,.05)', line: '#6b6b6b', swatch: '#6b6b6b' },
  { key: 'blue',   bg: '#ffffff', bar: 'rgba(0,0,0,.05)', line: '#949494', swatch: '#949494' },
  { key: 'violet', bg: '#ffffff', bar: 'rgba(0,0,0,.05)', line: '#b8b8b8', swatch: '#b8b8b8' },
  { key: 'rose',   bg: '#ffffff', bar: 'rgba(0,0,0,.05)', line: '#d9d9d9', swatch: '#d9d9d9' },
];

/* Dark Glow: the first palette in this app that sits on a near-black ground, so
   every fill is a low-opacity tint rather than a pastel — a light-mode note
   colour glares here. All six set `text`, because the ground is dark whichever
   one is chosen, and all six set `swatch`: a 16%-opacity tint over #050506 is
   nearly black, and six near-black legend chips would make the colour filter
   unusable. The swatch is the saturated colour the tint is made from. */
export const DARKGLOW_NOTE_PALETTE = [
  { key: 'slate',  bg: 'rgba(138,143,152,.16)', bar: 'rgba(255,255,255,.07)', line: 'rgba(138,143,152,.34)', text: '#EDEDEF', swatch: '#8A8F98' },
  { key: 'amber',  bg: 'rgba(224,166,63,.16)',  bar: 'rgba(255,255,255,.07)', line: 'rgba(224,166,63,.34)',  text: '#EDEDEF', swatch: '#E0A63F' },
  { key: 'green',  bg: 'rgba(53,201,155,.15)',  bar: 'rgba(255,255,255,.07)', line: 'rgba(53,201,155,.34)',  text: '#EDEDEF', swatch: '#35C99B' },
  { key: 'blue',   bg: 'rgba(94,106,210,.20)',  bar: 'rgba(255,255,255,.07)', line: 'rgba(94,106,210,.38)',  text: '#EDEDEF', swatch: '#5E6AD2' },
  { key: 'violet', bg: 'rgba(167,139,250,.18)', bar: 'rgba(255,255,255,.07)', line: 'rgba(167,139,250,.36)', text: '#EDEDEF', swatch: '#A78BFA' },
  { key: 'rose',   bg: 'rgba(242,104,107,.16)', bar: 'rgba(255,255,255,.07)', line: 'rgba(242,104,107,.34)', text: '#EDEDEF', swatch: '#F2686B' },
];

/* Sprightly: sticker paper. Saturated enough to read as a colour choice rather
   than a tint, light enough to take slate-800 text at AA. `line` is the same ink
   for all six because the chunky dark outline is the style's signature, exactly
   as in Sketch — the fill carries the identity. */
export const SPRIGHTLY_NOTE_PALETTE = [
  { key: 'slate',  bg: '#E2E8F0', bar: 'rgba(30,41,59,.10)', line: '#1E293B' },
  { key: 'amber',  bg: '#FDE68A', bar: 'rgba(30,41,59,.10)', line: '#1E293B' },
  { key: 'green',  bg: '#A7F3D0', bar: 'rgba(30,41,59,.10)', line: '#1E293B' },
  { key: 'blue',   bg: '#BFDBFE', bar: 'rgba(30,41,59,.10)', line: '#1E293B' },
  { key: 'violet', bg: '#DDD6FE', bar: 'rgba(30,41,59,.10)', line: '#1E293B' },
  { key: 'rose',   bg: '#FBCFE8', bar: 'rgba(30,41,59,.10)', line: '#1E293B' },
];

export const DESIGN_FIELDS = [
  { key: 'headingFont', prop: '--ui-heading', kind: 'font',   label: 'Headings' },
  { key: 'bodyFont',    prop: '--ui-body',    kind: 'font',   label: 'Body text' },
  { key: 'accent',      prop: '--accent',     kind: 'colour', label: 'Accent' },
  { key: 'radius',      prop: '--radius',     kind: 'px',     label: 'Corner rounding' },
  { key: 'ornament',    prop: '--ui-ornament', kind: 'ratio', label: 'Ornament' },
];

export const FONT_CHOICES = [
  { key: 'sans',   name: 'Lexend',      css: '"Lexend", system-ui, sans-serif' },
  { key: 'serif',  name: 'Fraunces',    css: '"Fraunces", Georgia, serif' },
  { key: 'system', name: 'System sans', css: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif' },
  { key: 'georgia', name: 'System serif', css: 'Iowan Old Style, Georgia, "Times New Roman", serif' },
  { key: 'mono',   name: 'Monospace',   css: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' },
];

export const DESIGN_DEFAULTS = () => ({
  headingFont: null, bodyFont: null, accent: null, radius: null, ornament: null,
});
